import "../asset/css/selectSearch.css";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import moment from "moment";
import MainLayout from "../layout/main";
import {
	createCustomer,
	getCustomer,
	updateCustomer
} from "../service/callAPI";
import Schema from "../type/schema";
import { useLoadState } from "../component/hook";
import { toast } from "react-toastify";
import { Button, Col, Form, Row } from "react-bootstrap";
import ValidatorSpan from "../component/validatorSpan";
import { labelStyle } from "../theme/default";
import { cacheCustomers } from "../service/cache";

type CustomerFormInputs = {
	id: string;
	name: string;
	dob: string;
	phone: string;
	email: string;
	address: string;
};

export default function CustomerEditPage() {
	const { customerId } = useParams<{ customerId: string }>();

	return (
		<MainLayout pageTitle="Khách Hàng" title={`Khách ${customerId || "Mới"}`}>
			<CustomerForm customerId={customerId} />
		</MainLayout>
	);
}

export const CustomerForm = ({
	customerId,
	shouldRedirect = true
}: {
	customerId?: string;
	shouldRedirect?: boolean;
}) => {
	const [isLoading, setIsLoading] = useState(false);

	const { register, handleSubmit, formState, setValue } =
		useForm<CustomerFormInputs>();
	const { isDirty, errors } = formState;
	const onSubmit: SubmitHandler<CustomerFormInputs> = async (data) => {
		try {
			setIsLoading(true);
			const converted = await finalConvert(data);
			if (!converted.id) {
				await handleCreateCustomer(converted, shouldRedirect);
				return;
			}
			await handleUpdateCustomer(converted, shouldRedirect);
			cacheCustomers();
		} catch (e: any) {
			toast.error(`Lỗi lưu bài|${e.toString()}`);
		} finally {
			setIsLoading(false);
		}
	};

	const fetcher = useCallback(() => {
		if (!customerId) {
			return;
		}
		return getCustomer(customerId);
	}, [customerId]);
	const callback = useCallback(
		(data: Schema.Customer) => {
			const converted = initialConvert(data);
			console.log("INITIALCONVERRTED", converted);
			for (let [key, val] of Object.entries(converted)) {
				setValue(key as any, val);
			}
		},
		[setValue]
	);
	useLoadState(fetcher, callback);

	return (
		<Form
			className="shadow"
			style={{
				backgroundColor: "white",
				padding: "20px",
				borderRadius: "0.35rem"
			}}
			onSubmit={handleSubmit(onSubmit)}
		>
			<Row>
				<Col sm={6}>
					<Form.Group>
						<Form.Label style={labelStyle}>Tên:</Form.Label>
						<Form.Control {...register("name")} />
						{errors.name && (
							<ValidatorSpan message={"Không được để trống trường này"} />
						)}
					</Form.Group>

					<Form.Group>
						<Form.Label style={labelStyle}>Điện thoại:</Form.Label>
						<Form.Control {...register("phone")} />
						{errors.phone && (
							<ValidatorSpan message={"Không được để trống trường này"} />
						)}
					</Form.Group>

					<Form.Group>
						<Form.Label style={labelStyle}>Email:</Form.Label>
						<Form.Control {...register("email")} />
						{errors.email && (
							<ValidatorSpan message={"Không được để trống trường này"} />
						)}
					</Form.Group>
				</Col>
				<Col sm={6}>
					<Form.Group>
						<Form.Label style={labelStyle}>Ngày sinh:</Form.Label>
						<Form.Control type="date" {...register("dob")} />
					</Form.Group>

					<Form.Group>
						<Form.Label style={labelStyle}>Địa chỉ:</Form.Label>
						<Form.Control {...register("address")} />
					</Form.Group>
				</Col>
			</Row>

			<div style={{ textAlign: "center" }}>
				<Button
					className="m-2"
					variant="primary"
					type="submit"
					disabled={!isDirty}
				>
					{isLoading ? "Đang lưu..." : "Lưu"}
				</Button>
				<a className="m-2 btn btn-danger" href="/customer">
					Quay Lại
				</a>
			</div>
		</Form>
	);
};

function initialConvert(data: Schema.Customer): CustomerFormInputs {
	const dob = data.dob && moment.unix(+data.dob!).format("YYYY-MM-DD");
	return { ...data, dob } as unknown as CustomerFormInputs;
}

async function finalConvert(
	data: CustomerFormInputs
): Promise<Schema.Customer> {
	const dob = data.dob && +moment(data.dob).format("X");

	return {
		...data,
		dob
	} as unknown as Schema.Customer;
}

async function handleCreateCustomer(
	payload: Schema.Customer,
	shouldRedirect: boolean
): Promise<void> {
	const resp = await createCustomer(payload);
	toast.info("Tạo khách hàng thành công");
	shouldRedirect &&
		setTimeout(() => (window.location.href = `/customer/${resp.id}`), 5000);
}

async function handleUpdateCustomer(
	payload: Schema.Customer,
	shouldRedirect: boolean
): Promise<void> {
	const resp = await updateCustomer(payload);
	toast.info("Cập nhật khách hàng thành công");
	shouldRedirect &&
		setTimeout(() => (window.location.href = `/customer/${resp.id}`), 5000);
}
